import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  Loader2,
  Check,
  Mail,
  MapPin,
  Instagram,
} from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useT } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const t = useT();
  const root = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError(t("contact.form.required"));
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          t("contact.form.genericError")
      );
    }
  };

  return (
    <div ref={root} className="pt-32 md:pt-40">
      <section className="container-x py-20 md:py-24">
        <p className="reveal eyebrow">{t("contact.eyebrow")}</p>
        <h1 className="reveal reveal-delay-1 mt-8 font-serif text-5xl md:text-7xl lg:text-[6rem] tracking-tight text-[#1A1A1A] leading-[0.95]">
          {t("contact.titleA")}{" "}
          <em className="text-[#9A7E47] font-light italic">{t("contact.titleB")}</em>
        </h1>
        <p className="reveal reveal-delay-2 mt-8 text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
          {t("contact.intro")}
        </p>
      </section>

      <section className="container-x pb-32 md:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7">
            {status === "success" ? (
              <div
                className="surface elev-1 p-10 border-[#B89B5E]/30"
                data-testid="contact-success"
              >
                <div className="flex items-center gap-3 text-[#9A7E47]">
                  <Check size={18} />
                  <span className="eyebrow">
                    {t("contact.form.successEyebrow")}
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl text-[#1A1A1A]">
                  {t("contact.form.successTitleA")}
                  {form.name.split(" ")[0]}.
                </h3>
                <p className="text-[#4A4A4A] mt-4 leading-relaxed">
                  {t("contact.form.successBody")}
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="space-y-10 surface elev-1 p-8 md:p-12"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                      {t("contact.form.nameLabel")}
                    </label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      className="field mt-2"
                      placeholder={t("contact.form.namePh")}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                      {t("contact.form.emailLabel")}
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      className="field mt-2"
                      placeholder={t("contact.form.emailPh")}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                    {t("contact.form.subjectLabel")}
                  </label>
                  <input
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="field mt-2"
                    placeholder={t("contact.form.subjectPh")}
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                    {t("contact.form.messageLabel")}
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    className="field mt-2 resize-none"
                    placeholder={t("contact.form.messagePh")}
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm" data-testid="contact-error">
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-between flex-wrap gap-4">
                  <button
                    type="submit"
                    className="btn-ink"
                    disabled={status === "loading"}
                    data-testid="contact-submit"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />{" "}
                        {t("contact.form.sending")}
                      </>
                    ) : (
                      <>
                        {t("contact.form.submit")} <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                  <p className="text-[#707070] text-xs uppercase tracking-wider">
                    {t("contact.form.replied")}
                  </p>
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 lg:pl-8">
            <div className="reveal eyebrow">{t("contact.aside.eyebrow")}</div>
            <h3 className="reveal reveal-delay-1 mt-6 font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">
              {t("contact.aside.title")}
            </h3>

            <div className="reveal reveal-delay-2 mt-10 space-y-7">
              <a
                href="mailto:eva@sanitybodymindspirit.com"
                className="flex items-start gap-5 group"
                data-testid="contact-email-link"
              >
                <Mail size={20} className="text-[#9A7E47] mt-1" />
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                    {t("contact.aside.emailLabel")}
                  </p>
                  <p className="text-[#1A1A1A] text-lg mt-1 group-hover:text-[#9A7E47] transition-colors">
                    eva@sanitybodymindspirit.com
                  </p>
                </div>
              </a>
              <div className="flex items-start gap-5">
                <MapPin size={20} className="text-[#9A7E47] mt-1" />
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                    {t("contact.aside.basedLabel")}
                  </p>
                  <p className="text-[#1A1A1A] text-lg mt-1">
                    {t("contact.aside.basedValue")}
                  </p>
                </div>
              </div>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-5 group"
                data-testid="contact-instagram-link"
              >
                <Instagram size={20} className="text-[#9A7E47] mt-1" />
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#707070]">
                    {t("contact.aside.followLabel")}
                  </p>
                  <p className="text-[#1A1A1A] text-lg mt-1 group-hover:text-[#9A7E47] transition-colors">
                    @sanitybodymindspirit
                  </p>
                </div>
              </a>
            </div>

            <div className="hairline mt-14" />

            <p className="reveal reveal-delay-3 mt-10 font-serif italic text-[#4A4A4A] leading-relaxed">
              {t("contact.aside.quote")}
            </p>
          </aside>
        </div>
      </section>
    </div>
  );
}
